import React from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { formatMass } from "@signolteam/shared/units-of-measure";
import { convertFuelBurnedToCo2 } from "@signolteam/shared/utils";
import { AppUtils } from "@src/utils";
import { useDCContext } from "@src/context";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

import CardContainer from "@src/components/cards/CardContainer";
import HorizontalLayout from "@src/components/cards/HorizontalLayout";
import { AnimateNumberCount, ImageWithRetry } from "@src/components/atoms";

const BestEventCard = ({
  bestEvent,
  handleClick,
  industry,
  padding = 0,
  trackingActionKey,
}) => {
  const { getDynamicReplacement } = useDCContext();
  if (!bestEvent) return null;

  const calculateEventSavingsCo2Kg = (bestEvent) => {
    let co2Saved = 0;

    if (!!bestEvent.totalCO2SavedKg) {
      co2Saved = bestEvent.totalCO2SavedKg;
    } else if (!!bestEvent.totalFuelSavedKg) {
      co2Saved = convertFuelBurnedToCo2(bestEvent.totalFuelSavedKg);
    } else if (!!bestEvent.savings) {
      co2Saved = convertFuelBurnedToCo2(bestEvent.savings);
    } else if (!!bestEvent.totalFuelSaved) {
      co2Saved = convertFuelBurnedToCo2(bestEvent.totalFuelSaved);
    } else {
      let co2FromBehaviours = 0;
      (bestEvent.behaviours || []).forEach((bvr) => {
        const co2Saved =
          bvr.CO2SavedKg ??
          convertFuelBurnedToCo2(bvr.fuelSavedKg || bvr.fuelSaved);
        if (bvr.succeeded && co2Saved > 0) {
          co2FromBehaviours += co2Saved;
        }
      });
      co2Saved = convertFuelBurnedToCo2(co2FromBehaviours);
    }
    return formatMass(industry, co2Saved).formattedValue;
  };

  const imgUrl = getDynamicReplacement("captain__overview__best_event_img");
  const totalCo2 = calculateEventSavingsCo2Kg(bestEvent);
  if (!totalCo2 || !imgUrl) {
    return null;
  }

  const routeInfo = AppUtils.returnJourneyString(
    bestEvent.origin,
    bestEvent.destination,
  );
  const dateInfo = moment
    .utc(bestEvent.ocurrenceDatetime)
    .format("DD MMM, YYYY");

  return (
    <CardContainer
      bgColor={"primary-300"}
      padding={padding ? padding : 0}
      onClick={() => {
        if (handleClick) {
          handleClick(
            `/dashboard/events/details?eventId=${bestEvent.id}`,
            "BEST_EVENT_SELECTION",
          );
        }
      }}
      trackingObject={
        !!trackingActionKey ? { actionKey: trackingActionKey } : null
      }
    >
      <HorizontalLayout ratio={[1, 2]} px={0} py={2} gap={2} className="w-full">
        {/* child 1 */}
        <ImageWithRetry
          src={imgUrl}
          alt=""
          className="h-full w-[80px] m-auto flex items-center "
        />
        {/* child 2 */}
        <div className="pr-4">
          <p className="text-base font-bold mb-2">Your Best Performance</p>
          <p className="text-sm font-medium mb-1">{routeInfo}</p>
          <p className="text-sm font-medium mb-6">{dateInfo}</p>
          <p className="text-sm font-medium">
            <span className="text-2xl font-extrabold">
              <AnimateNumberCount total={totalCo2} />{" "}
            </span>
            CO<sub>2</sub> saved
          </p>
        </div>
      </HorizontalLayout>
    </CardContainer>
  );
};

export const bestEventCardControls = {
  industry: COMMON_CONTROLS.industry,
  bestEvent: {
    propType: PropTypes.object,
    description: "Best event data object",
    controlType: "object",
  },
  handleClick: {
    propType: PropTypes.func,
    description: "Click handler for the card",
    disable: true,
  },
  padding: {
    propType: PropTypes.number,
    controlType: "select",
    options: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  },
  trackingActionKey: COMMON_CONTROLS.trackingActionKey,
};

BestEventCard.propTypes = AppUtils.getPropTypeFromControls(
  bestEventCardControls,
);

export default BestEventCard;
